import { inject, Injectable } from '@angular/core';
import { ToolName } from '../../cross-team-shared/tools-names';
import { ImageContentConnector as ImageContentConnectorInterface } from '../../team-apis/content/content-connector';
import { Answer } from '../../team-apis/otterdam/answers/answer';
import { Task, TaskExtras } from '../../team-apis/otterdam/task';
import { AbstractContentConnector2D } from '../shared/abstract-content-connector-2d';
import { ImageContentStateUndoService } from './state/image-content-state-undo.service';
import { ImageContentStore, isRawMaskBlank } from './state/image-content-store';
import { ImageAssetInitializationService } from './utils/loading/image-asset-initialization.service';
import { RasterMaskSavingService } from './utils/raster-mask-saving.service';

@Injectable()
export class ImageContentConnector
  extends AbstractContentConnector2D
  implements ImageContentConnectorInterface
{
  protected override store!: ImageContentStore;
  protected assetInitializationService = inject(
    ImageAssetInitializationService,
  );
  protected override undoService = inject(ImageContentStateUndoService);
  private rasterMaskSavingService = inject(RasterMaskSavingService);

  canRedo() {
    return this.undoService.canRedo();
  }

  canUndo() {
    return this.undoService.canUndo();
  }

  override async loadTask(task: Task) {
    const result = await super.loadTask(task);

    this.rasterMaskSavingService.setLastSavedMask(this.store.state().rawMask);
    return result;
  }

  override onAnnotationFocusChange(hasFocus: boolean) {
    this.store('hasFocus').set(hasFocus);
  }

  override async prepareForSave(isDraft: boolean) {
    if (!this.isRaster() || !this.shouldSaveMask()) {
      return undefined;
    }
    if (!isDraft && isRawMaskBlank(this.store.getRawMask())) {
      return {};
    }

    return this.saveMaskAnswer(isDraft);
  }

  override isOutputBlank() {
    if (this.store.state().status !== 'ready') {
      return true;
    }
    if (this.isRaster()) {
      const mask = this.store.state().rawMask;
      return !!mask && isRawMaskBlank(mask);
    }

    return super.isOutputBlank();
  }

  loadMask(answer: Answer, taskExtras?: TaskExtras) {
    this.assetInitializationService.loadMaskTrigger$.next({
      answer,
      taskExtras,
    });
  }

  override setTool(_tool: ToolName, _mode?: string): void {
    throw new Error('Method not implemented.');
  }

  private async saveMaskAnswer(isDraft: boolean) {
    let getUrl: string | undefined;
    if (isDraft) {
      getUrl = await this.rasterMaskSavingService.saveDraftToS3();
    } else {
      getUrl = await this.rasterMaskSavingService.saveToS3();
    }

    return (
      getUrl && {
        layers: { raster_coding: { mask_url: getUrl } },
      }
    );
  }

  private shouldSaveMask() {
    return (
      !!this.store.state().rawMask && this.store.state().status === 'ready'
    );
  }

  private isRaster() {
    return this.store.state().output.meta.layer_type === 'raster_coding';
  }
}
